// components/Financeiro / Listar - Financeiro.js
import React, { useState, useCallback, useEffect, memo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import { classNames } from 'primereact/utils';
import { FilterMatchMode } from 'primereact/api';
import { useHistory } from 'react-router-dom';
import FinanceiroService from '../../service/FinanceiroService';
import AuthorizationService from '../../service/AuthorizationService';
import { showToast } from '../ToastManager';

const financeiroService = new FinanceiroService();

const ListarTaxasPorEmpresa = memo(() => {
    const iss = localStorage.getItem('iss');
    const history = useHistory();

    const [filterInicio, setFilterInicio] = useState(null);
    const [filterFim, setFilterFim] = useState(null);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [first, setFirst] = useState(0);
    const [rowsState, setRowsState] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [toggleDialog, setToggleDialog] = useState(false);
    const [selectedFinanceiro, setSelectedFinanceiro] = useState(null);
    const [expandedRows, setExpandedRows] = useState({});


    const [totalGeral, setTotalGeral] = useState({
        procedimentos: 0,
        valorBruto: 0,
        taxasRecebidas: 0,
        valorLiquido: 0,
    });

    const formatDate = (date) => {
        if (!date) return null;
        const d = new Date(date);
        return d.toISOString().split('T')[0];
    };

    const currencyFormatter = (value) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(value || 0);
    };

    const numberFormatter = (value) => {
        return new Intl.NumberFormat('pt-BR').format(value || 0);
    };

    const statusBodyTemplate = (rowData) => (
        <Tag
            value={rowData.ativo ? 'Ativo' : 'Inativo'}
            severity={rowData.ativo ? 'success' : 'warning'}
        />
    );

    const actionBodyTemplate = (rowData) => {
        const handleEdit = () => {
            history.push(`/financeiro/editar/${rowData.id}`);
        };

        const handleDelete = (e) => {
            e.stopPropagation();
            setSelectedFinanceiro(rowData);
            setDeleteDialog(true);
        };

        const handleToggle = (e) => {
            e.stopPropagation();
            setSelectedFinanceiro(rowData);
            setToggleDialog(true);
        };

        return (
            <div className="flex gap-2">
                {AuthorizationService.hasPermission('financeiro.editar') && (
                    <Button
                        icon="pi pi-pencil"
                        severity="info"
                        rounded
                        size="small"
                        onClick={handleEdit}
                        tooltip="Editar"
                    />
                )}
                {AuthorizationService.hasPermission('financeiro.deletar') && (
                    <Button
                        icon="pi pi-trash"
                        severity="danger"
                        rounded
                        size="small"
                        onClick={handleDelete}
                        tooltip="Excluir"
                    />
                )}
                {AuthorizationService.hasPermission('financeiro.ativar') && (
                    <Button
                        icon={rowData.ativo ? 'pi pi-times' : 'pi pi-check'}
                        severity={rowData.ativo ? 'warning' : 'success'}
                        rounded
                        size="small"
                        onClick={handleToggle}
                        tooltip={rowData.ativo ? 'Inativar' : 'Ativar'}
                    />
                )}
            </div>
        );
    };

    const expandedRowTemplate = (data) => {
        return (
            <div className="p-3">
                <h5>{data.empresa} - Prestadores</h5>
                <DataTable value={data.financeiro} className="p-datatable-sm">
                    <Column field="prestador" header="Prestador" />
                    <Column field="total_procedimentos" header="Total Procedimentos" body={(rowData) => numberFormatter(rowData.total_procedimentos)} />
                    {/* <Column field="valor_bruto" header="Valor Bruto" body={(rowData) => currencyFormatter(rowData.valor_bruto)} /> */}
                    <Column field="taxas_recebidas" header="Taxas Recebidas" body={(rowData) => currencyFormatter(rowData.taxas_recebidas)} />
                    {/* <Column field="valor_liquido" header="Valor Líquido" body={(rowData) => currencyFormatter(rowData.valor_liquido)} /> */}
                </DataTable>
            </div>
        );
    };

    const onRowToggle = (e) => {
        setExpandedRows(e.data);
    };

    const loadData = useCallback(async () => {
        if (!filterInicio || !filterFim || !iss) return;

        setLoading(true);
        try {
            const params = {
                dataInicio: formatDate(filterInicio),
                dataFim: formatDate(filterFim),
            };

            const response = await financeiroService.listResumoTaxas(iss, params);

            const aggregated = response.data.data.map((item) => {
                const totalProc = item.financeiro.reduce((acc, curr) => acc + (curr.total_procedimentos || 0), 0);
                const totalTax = item.financeiro.reduce((acc, curr) => acc + (curr.taxas_recebidas || 0), 0);
                const totalBruto = item.financeiro.reduce((acc, curr) => acc + (curr.valor_bruto || 0), 0);
                const totalLiquido = item.financeiro.reduce((acc, curr) => acc + (curr.valor_liquido || 0), 0);
                return {
                    id: `emp-${item.empresa}`,
                    empresa: item.empresa,
                    total_procedimentos: totalProc,
                    valor_bruto: totalBruto,
                    total_taxas_recebidas: totalTax,
                    valor_liquido: totalLiquido,
                    ativo: item.ativo !== undefined ? item.ativo : true,
                    financeiro: item.financeiro, // Mantém os dados dos prestadores
                };
            });

            // Calcular totais gerais
            const totalProc = aggregated.reduce((acc, curr) => acc + curr.total_procedimentos, 0);
            const totalBruto = aggregated.reduce((acc, curr) => acc + curr.valor_bruto, 0);
            const totalTax = aggregated.reduce((acc, curr) => acc + curr.total_taxas_recebidas, 0);
            const totalLiq = aggregated.reduce((acc, curr) => acc + curr.valor_liquido, 0);

            setTotalGeral({
                procedimentos: totalProc,
                valorBruto: totalBruto,
                taxasRecebidas: totalTax,
                valorLiquido: totalLiq,
            });

            setData(aggregated);
            setTotalRecords(response.pagination?.total || aggregated.length);
        } catch (error) {
            showToast('error', error.message || 'Erro ao carregar dados de financeiro');
        } finally {
            setLoading(false);
        }
    }, [filterInicio, filterFim, iss]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const hideDeleteDialog = () => setDeleteDialog(false);

    const confirmDeleteFinanceiro = useCallback(async () => {
        if (!selectedFinanceiro) return;
        try {
            await FinanceiroService.excluir(iss, selectedFinanceiro.id);
            showToast('success', 'Financeiro excluído com sucesso!');
            loadData();
        } catch (error) {
            showToast('error', error.message || 'Erro ao excluir financeiro');
        }
        hideDeleteDialog();
    }, [selectedFinanceiro, iss, loadData]);

    const hideToggleDialog = () => setToggleDialog(false);

    const confirmToggle = useCallback(async () => {
        if (!selectedFinanceiro) return;
        try {
            await FinanceiroService.ativar(iss, selectedFinanceiro.id, !selectedFinanceiro.ativo);
            showToast('success', `Financeiro ${!selectedFinanceiro.ativo ? 'ativado' : 'inativado'} com sucesso!`);
            loadData();
        } catch (error) {
            showToast('error', error.message || 'Erro ao alterar status');
        }
        hideToggleDialog();
    }, [selectedFinanceiro, iss, loadData]);

    return (
        <div className="grid">
            <div className="col-12">
                <div className="grid">
                    <div className="field col-6 md:col-4">
                        <label htmlFor="dataInicio">Data Início *</label>
                        <Calendar
                            id="dataInicio"
                            value={filterInicio}
                            onChange={(e) => setFilterInicio(e.value)}
                            dateFormat="dd/mm/yy"
                            showIcon
                            className={classNames({ 'p-invalid': !filterInicio })}
                        />
                    </div>
                    <div className="field col-6 md:col-4">
                        <label htmlFor="dataFim">Data Fim *</label>
                        <Calendar
                            id="dataFim"
                            value={filterFim}
                            onChange={(e) => setFilterFim(e.value)}
                            dateFormat="dd/mm/yy"
                            showIcon
                            className={classNames({ 'p-invalid': !filterFim })}
                        />
                    </div>
                </div>
            </div>
            <div className="col-12">
                <div className="flex justify-content-end mb-3">
                    {AuthorizationService.hasPermission('financeiro.criar') && (
                        <Button
                            label="Novo"
                            icon="pi pi-plus"
                            onClick={() => history.push('/financeiro/novo')}
                        />
                    )}
                </div>
            </div>
            <div className="col-12">
                <div className="grid">
                    <div className="col-12 md:col-3">
                        <div className="card">
                            <h5>Total Procedimentos</h5>
                            <p className="text-3xl font-bold">{numberFormatter(totalGeral.procedimentos)}</p>
                        </div>
                    </div>
                    {/* <div className="col-12 md:col-3">
                        <div className="card">
                            <h5>Valor Bruto</h5>
                            <p className="text-3xl font-bold">{currencyFormatter(totalGeral.valorBruto)}</p>
                        </div>
                    </div> */}
                    <div className="col-12 md:col-3">
                        <div className="card">
                            <h5>Taxas Recebidas</h5>
                            <p className="text-3xl font-bold">{currencyFormatter(totalGeral.taxasRecebidas)}</p>
                        </div>
                    </div>
                    {/* <div className="col-12 md:col-3">
                        <div className="card">
                            <h5>Valor Líquido</h5>
                            <p className="text-3xl font-bold">{currencyFormatter(totalGeral.valorLiquido)}</p>
                        </div>
                    </div> */}
                </div>
            </div>
            <div className="col-12">
                <DataTable
                    value={data}
                    dataKey="id"
                    expandedRows={expandedRows}
                    onRowToggle={onRowToggle}
                    rowExpansionTemplate={expandedRowTemplate}
                    paginator
                    first={first}
                    rows={rowsState}
                    totalRecords={totalRecords}
                    onPage={(e) => {
                        setFirst(e.first);
                        setRowsState(e.rows);
                    }}
                    loading={loading}
                    responsiveLayout="scroll"
                    emptyMessage="Nenhum dado de financeiro encontrado para o período selecionado."
                    stripedRows
                    removableSort
                >
                    <Column expander={true} style={{ width: '4rem' }} />
                    <Column field="empresa"
                        header="Empresa"
                        sortable
                        filter
                        filterMatchMode={FilterMatchMode.CONTAINS}
                        filterPlaceholder="Buscar por empresa" />
                    <Column
                        field="total_procedimentos"
                        header="Total Procedimentos"
                        sortable
                        body={(rowData) => numberFormatter(rowData.total_procedimentos)}
                    />
                    <Column
                        field="total_taxas_recebidas"
                        header="Total Taxas Recebidas"
                        sortable
                        body={(rowData) => currencyFormatter(rowData.total_taxas_recebidas)}
                    />
                    {/* <Column
                        field="ativo"
                        header="Ativo"
                        body={statusBodyTemplate}
                    />
                    <Column
                        header="Opções"
                        body={actionBodyTemplate}
                        frozen
                        alignFrozen="right"
                    /> */}
                </DataTable>
            </div>

            {/* Dialog Excluir */}
            <Dialog
                visible={deleteDialog}
                header="Confirmação"
                onHide={hideDeleteDialog}
                footer={(
                    <div className="flex gap-2 justify-content-end">
                        <Button label="Não" onClick={hideDeleteDialog} />
                        <Button
                            label="Sim"
                            severity="danger"
                            onClick={confirmDeleteFinanceiro}
                        />
                    </div>
                )}
            >
                <p>Confirma a exclusão do financeiro <b>{selectedFinanceiro?.empresa}</b>?</p>
            </Dialog>

            {/* Dialog Ativar/Inativar */}
            <Dialog
                visible={toggleDialog}
                header="Confirmação"
                onHide={hideToggleDialog}
                footer={(
                    <div className="flex gap-2 justify-content-end">
                        <Button label="Cancelar" onClick={hideToggleDialog} />
                        <Button
                            label={selectedFinanceiro?.ativo ? 'Inativar' : 'Ativar'}
                            severity={selectedFinanceiro?.ativo ? 'warning' : 'success'}
                            onClick={confirmToggle}
                        />
                    </div>
                )}
            >
                <p>
                    Deseja <strong>{selectedFinanceiro?.ativo ? 'inativar' : 'ativar'}</strong> o financeiro{' '}
                    <strong>{selectedFinanceiro?.empresa}</strong>?
                </p>
            </Dialog>
        </div>
    );
}, () => true);

export default ListarTaxasPorEmpresa;